/**
 * ARVEXIS Conversation History — Shared Memories Overlay
 * =======================================================
 * 
 * FOUNDER VISION:
 * "A dedicated full-screen experience that opens from the companion view
 * as an overlay (not a separate application route and not a small modal/slide-out panel).
 * It should feel like opening Arvexis' shared memories rather than navigating
 * to another section of the app. This design should be able to evolve naturally
 * into the future Journey experience."
 * 
 * DESIGN PRINCIPLES:
 * - Full-screen overlay, not a route or small modal
 * - Feels like opening shared memories, not navigating
 * - Designed to evolve into the Journey experience
 * - Voice transcripts are grouped by time, not listed chronologically
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Clock, Calendar, ChevronRight, Loader2, Heart, Trash2 } from 'lucide-react';
import TalkRecoveryOffer from './TalkRecoveryOffer';
import DreamsShell from './DreamsShell';
import useFullScreenSurface from '../hooks/useFullScreenSurface';
import { memberTimeOptions } from '../utils/memberTime';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// TRANSCRIPT CARD — Individual saved conversation
// =============================================================================
const TranscriptCard = ({ transcript, onClick, onDelete, lang }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return lang === 'es' ? 'Hoy' : 'Today';
    if (diffDays === 1) return lang === 'es' ? 'Ayer' : 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { weekday: 'long' });
    
    return date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };
  
  // "1 min" beside a date reads as "one minute ago". Duration is always stated as a length.
  const formatDuration = (seconds) => {
    const label = lang === 'es' ? 'Duración' : 'Length';
    if (!seconds || seconds < 60) {
      return `${label}: ${lang === 'es' ? 'menos de un minuto' : 'less than a minute'}`;
    }
    const mins = Math.round(seconds / 60);
    if (mins <= 1) return `${label}: ${lang === 'es' ? 'un minuto aprox.' : 'about a minute'}`;
    return `${label}: ${lang === 'es' ? `${mins} minutos aprox.` : `about ${mins} minutes`}`;
  };
  
  // Get a preview of the conversation
  const getPreview = () => {
    if (transcript.summary) return transcript.summary;
    if (transcript.message_count) {
      return lang === 'es' 
        ? `${transcript.message_count} mensajes` 
        : `${transcript.message_count} messages`;
    }
    return lang === 'es' ? 'Conversación guardada' : 'Saved conversation';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="talks__card"
      data-testid="transcript-card"
    >
      {/* G2: chronology governs the row — date, approximate duration, message count, delete.
          No chevron, playback cue or open affordance is presented. The existing tap behaviour
          is unchanged. No conversation content is shown here. */}
      <div className="talks__glyph"><MessageCircle size={20} strokeWidth={1.5} /></div>

      <div className="talks__body">
        <div className="talks__line">
          <span className="talks__date">{formatDate(transcript.started_at)}</span>
          {transcript.duration_seconds > 0 && (
            <>
              <span className="talks__dot">·</span>
              <Clock className="talks__clock" strokeWidth={1.6} />
              <span className="talks__length">{formatDuration(transcript.duration_seconds)}</span>
            </>
          )}
        </div>
        <p className="talks__count" data-testid="transcript-message-count">
          {(() => {
            const n = Array.isArray(transcript.messages)
              ? transcript.messages.length
              : (transcript.message_count || 0);
            return lang === 'es' ? `${n} mensajes` : `${n} messages`;
          })()}
        </p>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onDelete(transcript); }}
        className="talks__delete"
        title={lang === 'es' ? 'Eliminar' : 'Delete'}
        aria-label={lang === 'es' ? 'Eliminar' : 'Delete'}
        data-testid="transcript-delete-btn"
      >
        <Trash2 size={18} strokeWidth={1.6} />
      </button>
    </motion.div>
  );
};

// =============================================================================
// TRANSCRIPT DETAIL VIEW — Full conversation view
// =============================================================================
const TranscriptDetail = ({ transcript, onBack, lang }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadMessages();
  }, [transcript.id]);
  
  const loadMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/realtime/transcript/${transcript.id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load transcript messages:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', memberTimeOptions({ 
      hour: 'numeric', 
      minute: '2-digit' 
    }));
  };
  
  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
        <button
          onClick={onBack}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="back-to-history-btn"
        >
          <ChevronRight className="w-5 h-5 text-white/60 rotate-180" />
        </button>
        <div className="flex-1">
          <h3 
            className="text-base text-white/80"
            style={{ fontFamily: '"Inter", sans-serif', fontWeight: 500 }}
          >
            {lang === 'es' ? 'Conversación' : 'Conversation'}
          </h3>
          <p className="text-xs text-white/40">{formatFullDate(transcript.started_at)}</p>
        </div>
      </div>
      
      {/* Messages — Updated to match CompanionChat visual redesign */}
      <div className="flex-1 overflow-y-auto px-5 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-amber-400/60 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-white/40 text-sm py-8">
            {lang === 'es' ? 'No hay mensajes para mostrar' : 'No messages to display'}
          </p>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={`msg-${idx}-${msg.timestamp || ''}`}
              className={`mb-6 ${msg.role === 'user' ? 'pl-8' : 'pr-8'}`}
            >
              {msg.role === 'user' ? (
                /* User message — Soft, understated card */
                <div className="flex justify-end">
                  <div
                    className="max-w-[85%] px-4 py-3 rounded-2xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '18px 18px 4px 18px'
                    }}
                  >
                    <p
                      className="leading-[1.6] whitespace-pre-wrap"
                      style={{ 
                        fontFamily: '"Inter", sans-serif',
                        color: 'rgba(255, 255, 255, 0.75)',
                        fontSize: '0.875rem'
                      }}
                    >
                      {msg.content}
                    </p>
                    {msg.timestamp && (
                      <p className="text-[10px] text-white/20 mt-1.5 text-right">
                        {formatTimestamp(msg.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                /* Arvexis message — Flowing text, no container */
                <div className="relative">
                  <p
                    className="leading-[1.7] whitespace-pre-wrap"
                    style={{ 
                      fontFamily: '"Inter", sans-serif',
                      color: 'rgba(255, 255, 255, 0.88)',
                      fontSize: '0.9375rem',
                      fontWeight: 400,
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {msg.content}
                  </p>
                  {msg.timestamp && (
                    <p className="text-[10px] text-white/20 mt-1.5">
                      {formatTimestamp(msg.timestamp)}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EMPTY STATE — When no saved conversations exist
// =============================================================================
const EmptyState = ({ lang }) => (
  <div className="flex flex-col items-center justify-center h-full px-8 text-center">
    {/* Subtle presence dot like empty chat state */}
    <div 
      className="w-3 h-3 rounded-full mb-6"
      style={{ 
        background: 'linear-gradient(135deg, #c9a55c 0%, #a08040 100%)',
        boxShadow: '0 0 20px rgba(180, 140, 60, 0.3), 0 0 40px rgba(180, 140, 60, 0.1)'
      }}
    />
    <h3 
      className="text-base text-white/50 mb-2"
      style={{ fontFamily: '"Inter", sans-serif' }}
    >
      {lang === 'es' ? 'Ninguna charla guardada aún' : 'No saved talks yet'}
    </h3>
    <p 
      className="text-sm text-white/35 max-w-xs"
      style={{ fontFamily: '"Inter", sans-serif' }}
    >
      {lang === 'es' 
        ? 'Cuando guardes una charla por voz, aparecerá aquí.'
        : 'When you keep a talk, it will appear here.'}
    </p>
  </div>
);

// =============================================================================
// MAIN COMPONENT — Conversation History Overlay
// =============================================================================
const ConversationHistory = ({ lang = 'en', onClose }) => {
  useFullScreenSurface(true, onClose);
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    loadTranscripts();
  }, []);
  
  // Add Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  const loadTranscripts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/realtime/transcripts`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranscripts(data.transcripts || []);
      }
    } catch (error) {
      console.error('Failed to load transcripts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // R4: "Keep" must never mean irreversible.
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/realtime/transcript/${pendingDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('delete failed');
      setTranscripts(prev => prev.filter(t => t.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (error) {
      console.error('Failed to delete transcript:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Group transcripts by time period
  const groupTranscripts = useCallback(() => {
    const now = new Date();
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };
    
    transcripts.forEach(t => {
      const date = new Date(t.started_at);
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) groups.today.push(t);
      else if (diffDays === 1) groups.yesterday.push(t);
      else if (diffDays < 7) groups.thisWeek.push(t);
      else if (diffDays < 30) groups.thisMonth.push(t);
      else groups.older.push(t);
    });
    
    return groups;
  }, [transcripts]);
  
  const groups = groupTranscripts();
  
  const getGroupLabel = (key) => {
    const labels = {
      today: lang === 'es' ? 'Hoy' : 'Today',
      yesterday: lang === 'es' ? 'Ayer' : 'Yesterday',
      thisWeek: lang === 'es' ? 'Esta semana' : 'This Week',
      thisMonth: lang === 'es' ? 'Este mes' : 'This Month',
      older: lang === 'es' ? 'Anteriores' : 'Earlier'
    };
    return labels[key];
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // the foreground is handed back at once: fading out cross-faded the still-mounted page beneath through this surface
      className="utility-overlay z-[160] utility-surface utility-surface--atmospheric"
      data-testid="conversation-history-overlay"
    >
      {/* Saved Talks is where a member comes looking for a Talk that got cut off, so the
          Keep / Let it disappear offer is presented here as well. Keeping one refreshes
          the list immediately. */}
      <TalkRecoveryOffer surface="saved_talks" onKept={loadTranscripts} />
      <DreamsShell lang={lang} activePath="/profile">
      <div className="flex flex-col h-full">
        {/* Header */}
        {/* G2: the authenticated utility-shell header family, shared with Message History and
            Shared Memories. Close destination unchanged — visual alignment only. */}
        <div
          className="flex items-start justify-between px-6 min-[1200px]:px-8 shrink-0"
          style={{
            paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 8px))',
            paddingBottom: '12px'
          }}
        >
          <span
            className="text-[13px] select-none"
            style={{ fontFamily: 'Outfit, Inter, system-ui, sans-serif', letterSpacing: '0.42em', textIndent: '0.42em', color: '#F4EFE7', margin: '0 auto', paddingLeft: '44px' }}
            data-testid="saved-talks-wordmark"
          >
            ARVEXIS
          </span>
          <button
            onClick={onClose}
            className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
            style={{ background: 'transparent', border: '1px solid rgba(232, 197, 143, 0.38)', color: 'rgba(232, 197, 143, 0.9)' }}
            aria-label={lang === 'es' ? 'Cerrar' : 'Close'}
            data-testid="close-history-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 min-[1200px]:px-8 pb-2 shrink-0">
          <p className="talks__eyebrow" data-testid="saved-talks-eyebrow">
            {lang === 'es' ? 'CHARLAS GUARDADAS' : 'SAVED TALKS'}
          </p>
          <h2
            className="text-[30px] leading-[36px] min-[1200px]:text-[44px] min-[1200px]:leading-[52px]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: '#F4EFE7' }}
            data-testid="saved-talks-title"
          >
            {lang === 'es' ? 'Charlas guardadas' : 'Saved Talks'}
          </h2>
          <p
            className="mt-2 text-[15px] leading-[22px] min-[1200px]:text-[17px]"
            style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#A8B3BF' }}
            data-testid="saved-talks-subtitle"
          >
            {lang === 'es'
              ? 'Charlas que elegiste guardar.'
              : 'Talks you chose to keep.'}
          </p>
        </div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          {selectedTranscript ? (
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1 overflow-hidden"
            >
              <TranscriptDetail
                transcript={selectedTranscript}
                onBack={() => setSelectedTranscript(null)}
                lang={lang}
              />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 overflow-y-auto"
            >
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-amber-400/50 animate-spin" />
                </div>
              ) : transcripts.length === 0 ? (
                <EmptyState lang={lang} />
              ) : (
                <div className="px-6 min-[1200px]:px-8 py-4 space-y-6 w-full talks__column">
                  {/* Render grouped transcripts */}
                  {['today', 'yesterday', 'thisWeek', 'thisMonth', 'older'].map(groupKey => (
                    groups[groupKey].length > 0 && (
                      <div key={groupKey}>
                        <h3
                          className="talks__group px-1"
                          data-testid={`saved-talks-group-${groupKey}`}
                        >
                          {getGroupLabel(groupKey)}
                        </h3>
                        <div
                          className="talks__stack"
                          data-testid={`saved-talks-ledger-${groupKey}`}
                        >
                          {groups[groupKey].map(transcript => (
                            <TranscriptCard
                              key={transcript.id}
                              transcript={transcript}
                              onClick={() => setSelectedTranscript(transcript)}
                              onDelete={setPendingDelete}
                              lang={lang}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </DreamsShell>

      {/* R4: delete confirmation */}
      <AnimatePresence>
        {pendingDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[220] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setPendingDelete(null)}
            data-testid="transcript-delete-modal"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-sm bg-gradient-to-br from-[#1A1A24] to-[#0F0F14] rounded-2xl border border-white/10 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                className="text-base mb-3"
                style={{ fontFamily: '"Cormorant Garamond", serif', color: 'rgba(255,255,255,0.9)' }}
                data-testid="transcript-delete-title"
              >
                {lang === 'es' ? '¿Eliminar esta conversación?' : 'Delete this conversation?'}
              </h3>
              <p
                className="text-sm leading-relaxed mb-6"
                style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.45)' }}
                data-testid="transcript-delete-explainer"
              >
                {lang === 'es'
                  ? 'La transcripción se elimina de forma permanente. Lo que Arvexis ha llegado a entender sobre ti permanece — la grabación se elimina, no la relación. Para que olvide algo, pídeselo directamente.'
                  : 'The transcript is permanently deleted. What Arvexis has come to understand about you remains — the recording is deleted, not the relationship. To have her forget something, ask her directly.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingDelete(null)}
                  className="flex-1 py-2.5 rounded-full text-sm text-white/50 hover:text-white/70 border border-white/10 transition-colors"
                  data-testid="transcript-delete-cancel"
                >
                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 py-2.5 rounded-full text-sm text-red-300/80 hover:text-red-300 border border-red-400/20 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  data-testid="transcript-delete-confirm"
                >
                  {isDeleting
                    ? (lang === 'es' ? 'Eliminando…' : 'Deleting…')
                    : (lang === 'es' ? 'Eliminar' : 'Delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <style>{`
        .talks__eyebrow {
          font-size: 11px; font-weight: 600; letter-spacing: 0.26em; text-transform: uppercase;
          color: #E8C58F; margin: 0 0 6px;
        }
        .talks__group {
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(232, 197, 143, 0.82); margin-bottom: 12px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .talks__column { max-width: 560px; margin-left: auto; margin-right: auto; }
        .talks__stack { display: flex; flex-direction: column; gap: 10px; }
        .talks__card {
          display: flex; align-items: center; gap: 14px; padding: 14px 16px;
          border-radius: 18px; background: rgba(255, 255, 255, 0.028);
          border: 1px solid rgba(244, 239, 231, 0.10); cursor: pointer;
          transition: background-color 180ms ease, border-color 180ms ease;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .talks__card:hover {
          background: rgba(255, 255, 255, 0.05); border-color: rgba(232, 197, 143, 0.26);
        }
        .talks__glyph {
          flex-shrink: 0; width: 44px; height: 44px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(232, 197, 143, 0.42); color: #E8C58F;
        }
        .talks__body { flex: 1; min-width: 0; }
        .talks__line { display: flex; align-items: center; gap: 7px; min-width: 0; }
        .talks__date { font-size: 15px; color: #F4EFE7; flex-shrink: 0; }
        .talks__dot { color: rgba(244, 239, 231, 0.32); }
        .talks__clock {
          width: 15px; height: 15px; flex-shrink: 0; color: rgba(244, 239, 231, 0.42);
        }
        .talks__length {
          font-size: 13.5px; color: rgba(244, 239, 231, 0.48);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .talks__count { margin-top: 4px; font-size: 13.5px; color: rgba(244, 239, 231, 0.48); }
        .talks__delete {
          flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          background: transparent; border: 1px solid rgba(244, 239, 231, 0.12);
          color: rgba(244, 239, 231, 0.6); cursor: pointer;
          transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease;
        }
        .talks__delete:hover {
          background: rgba(224, 106, 106, 0.10); border-color: rgba(224, 106, 106, 0.38);
          color: #E06A6A;
        }
        @media (min-width: 1200px) { .talks__column { max-width: 720px; } }
      `}</style>
    </motion.div>
  );
};

export default ConversationHistory;
